import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getPalletEdit } from "../../redux/slices/palletEdit.feature";
import { palstatEditDefectsColumnData } from "./PalstatEditDefectsColumnData";
import { palstatEditMovementsColumnData } from "./PalstatEditMovementsColumnData";
import { momentDtoToLocalStringByCulture } from "../../aplUtils/dateHelper";
import { DtGrid2 } from "../common/DtGrid2";
import AplSettingsSelector from "../../redux/helpers/AplSettingsSelector";
import { getSsccByOffset, sanitizeBarcode } from "../../aplApi/palletsHelper";
import { onlyOnEnter } from "../../aplUtils/aplDebounce";
import Spinner from "../common/Spinner";
import { tryCatchBootbox } from "../../aplApi/apiTryCatch";
import { closeModalRedux as closeModal, showModalWithPalletsByObject } from "../../redux/slices/modalPallets2.feature";
import { IRootState } from "../../types/redux/IRootStore";
import { DtGridPropsOptions2 } from "../../types/dtGridPropsOptions2";
import _ from "lodash";
import { ModalType } from "../common/Modals/types/modalType";
import { useTranslation } from "react-i18next";
import { getExceptionText, translateColumns, translateString } from "../../translations/gridTranslationHelper";
import { renderPalletCondition, renderPalletStatus } from "../../aplUtils/statusConditionEnumHelper";
import { getPalstatActiveButtons } from "../../aplUtils/permissions";
import { copyPallets } from "../../aplUtils/clipboard";
import { useAppDispatch } from "../../redux/hooks";
import { UserPermission } from "../../types/userPermission";
import { onSetSsccSearch } from "../../redux/slices/palletCache.feature";

export default function PalstatEdit() {
    const { t } = useTranslation();
    const params = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const palletEditRedux = useSelector((state: IRootState) => state.palletEdit);
    const defectTypes = useSelector((state: IRootState) => state.auth.defectTypes);
    const pallet = palletEditRedux.palletDetails[0];
    const aplSettings = AplSettingsSelector();
    const globalAdminPerm = aplSettings.hasPermAnyLine(UserPermission.GlobalPermsAdmin);
    const [printButtonShown, setPrintButtonShown] = React.useState(false);

    const defectGridOptions: DtGridPropsOptions2 = {
        data: pallet?.defects ?? [],
        columnData: translateColumns(palstatEditDefectsColumnData(aplSettings.siteId, aplSettings.culture, defectTypes), aplSettings.culture),
        loading: palletEditRedux.palletDetailsLoding,
        tableName: "tblPalletDetailsDefects",
        paging: false,
        showSpinner: !palletEditRedux.palletDetailsFirstLoaded,
        defaultSort: {
            field: ["defectId"],
            direction: ["asc"]
        },
        selectType: "none",
        colouring: false,
        headerFilters: false,
        tableClass: "table-palletetails-defects",
        rowId: "id",
        pageLength: 100,
        filters: {},
        setSelectedItems: _.noop,
        selectedItems: [],
        setFilters: _.noop,
        page: 1,
        setPage: _.noop
    };

    // const traceabilityGridOptions: DtGridPropsOptions2 = {
    //     data: [],
    //     columnData: translateColumns(palstatEditTraceabilityColumnData(aplSettings.siteId, aplSettings.culture), aplSettings.culture),
    //     loading: palletEditRedux.palletDetailsLoding,
    //     tableName: "tblPalletTraceability",
    //     paging: false,
    //     showSpinner: !palletEditRedux.palletDetailsFirstLoaded,
    //     defaultSort: {
    //         field: ["id"],
    //         direction: ["desc"]
    //     },
    //     selectType: "none",
    //     colouring: false,
    //     headerFilters: false,
    //     tableClass: "table-palletetails-defects",
    //     rowId: "id",
    //     pageLength: 100,
    //     filters: {},
    //     setSelectedItems: _.noop,
    //     selectedItems: [],
    //     setFilters: _.noop
    // };

    const movementGridOptions: DtGridPropsOptions2 = {
        data: pallet?.movementViews ?? [],
        columnData: translateColumns(
            palstatEditMovementsColumnData(aplSettings.siteId, aplSettings.culture, defectTypes),
            aplSettings.culture
        ),
        loading: palletEditRedux.palletDetailsLoding,
        tableName: "tblPalletDetailsMovements",
        paging: false,
        showSpinner: !palletEditRedux.palletDetailsFirstLoaded,
        defaultSort: {
            field: ["mvtDate"],
            direction: ["desc"]
        },
        selectType: "none",
        colouring: false,
        headerFilters: false,
        tableClass: "table-palletetails-movements",
        rowId: "movementId",
        pageLength: 100,
        filters: {},
        setSelectedItems: _.noop,
        selectedItems: [],
        setFilters: _.noop,
        page: 1,
        setPage: _.noop
    };

    const searchSccc = (barcode: string) => {
        if (sanitizeBarcode(barcode)) {
            navigate(`/palstat/edit/${aplSettings.siteId}/${sanitizeBarcode(barcode)}`);
        }
    };

    const getPalletByOffset = async (offset: number) => {
        tryCatchBootbox(async () => {
            var sscc = await getSsccByOffset(Number(params.siteId), params.sscc!, offset);
            if (sscc?.data) {
                navigate(`/palstat/edit/${aplSettings.siteId}/${sscc.data}`);
            }
        });
    };

    const palletCacheRedux = useSelector((state: IRootState) => state.palletCache);
    const setModalVisibility = (modalType: ModalType) => {
        if (modalType === ModalType.None) {
            dispatch(closeModal());
        }
        dispatch(showModalWithPalletsByObject({ pallets: [pallet], modalType: modalType }) as any);
    };

    const buttonsOptions = () => {
        let opts = getPalstatActiveButtons(palletEditRedux.palletDetails, false, aplSettings.isUsSite);

        return {
            ...opts,
            palletDetailsClick: () => {
                navigate(`/palstat/edit/${palletCacheRedux.selectedPallets[0].siteId}/${palletCacheRedux.selectedPallets[0].sscc}`);
            },
            blockClick: () => {
                setModalVisibility(ModalType.Defects);
            },
            unblockClick: () => {
                setModalVisibility(ModalType.Release);
            },
            reprintClick: () => {
                setModalVisibility(ModalType.Reprint);
            },
            scrapClick: () => {
                setModalVisibility(ModalType.Scrap);
            },
            deleteClick: () => {
                setModalVisibility(ModalType.Delete);
            },
            partPalletClick: () => {
                setModalVisibility(ModalType.PartPallet);
            },
            copySsccClick: () => {
                copyPallets(palletCacheRedux.selectedPallets);
            },
            hideScrap: aplSettings.isUsSite
        };
    };

    useEffect(() => {
        let p = { siteId: Number(params.siteId), sscc: params.sscc! };
        dispatch(getPalletEdit(p) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.siteId, params.sscc]);

    if (!pallet && palletEditRedux.palletDetailsLoding) {
        return <Spinner visible={true} marginsClass=""></Spinner>;
    } else if (!pallet && !palletEditRedux.palletDetailsLoding && palletEditRedux.palletDetailsError) {
        return (
            <React.Fragment>
                <div className="container mw-100">
                    {/* <div className="row">
                        <div className="col-8">
                        </div>
                        <div className="col-4">
                            <span className="float-right">
                                <div className="input-group mb-3">
                                    <input
                                        type="text"
                                        id="txtSsccSearch"
                                        className="form-control"
                                        placeholder="SSCC"
                                        aria-label="sscc"
                                        aria-describedby="sscc-search"
                                        ref={searchSsccRef as any}
                                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                            onlyOnEnter(e, () => {
                                                searchSccc(e.currentTarget.value);
                                            });
                                        }}
                                    />
                                    <div className="input-group-append">
                                        <button
                                            id="btnSsccSearch"
                                            className="btn  btn-primary"
                                            type="button"
                                            onClick={e => {
                                                searchSccc(searchSsccRef.current!.value);
                                            }}
                                        >
                                            <span
                                                id="btnRefreshSpinner"
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-hidden="true"
                                                style={{ display: "none" }}
                                            ></span>
                                            {t("main.search")}
                                        </button>
                                    </div>
                                </div>
                            </span>
                        </div>
                    </div> */}
                    <div id="div-error" className={`alert alert-danger ${palletEditRedux.palletDetailsError ? "" : "hide"}`} role="alert">
                        {params.sscc} {getExceptionText(palletEditRedux.palletDetailsError, aplSettings.culture)}
                    </div>

                    <div className="row">
                        <div className="col-4"></div>
                        <div className="col-4">
                            <span>
                                <div className="input-group mb-3">
                                    <input
                                        type="text"
                                        id="txtSsccSearch"
                                        className="form-control"
                                        placeholder="SSCC"
                                        aria-label="sscc"
                                        aria-describedby="sscc-search"
                                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                            onlyOnEnter(e, () => {
                                                searchSccc(e.currentTarget.value);
                                            });
                                        }}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            dispatch(onSetSsccSearch(e.currentTarget.value));
                                        }}
                                    />
                                    <div className="input-group-append">
                                        <button
                                            id="btnSsccSearch"
                                            className="btn  btn-primary"
                                            type="button"
                                            onClick={e => {
                                                searchSccc(palletCacheRedux.palletSearch.ssccSearch);
                                            }}
                                        >
                                            <span
                                                id="btnRefreshSpinner"
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-hidden="true"
                                                style={{ display: "none" }}
                                            ></span>
                                            {t("main.search")}
                                        </button>
                                        <button
                                            className="btn  btn-primary"
                                            type="button"
                                            onClick={() => navigate(-1)}
                                            id="btn-search-error-back"
                                        >
                                            {t("main.back")}
                                        </button>
                                    </div>
                                </div>
                            </span>
                        </div>
                        <div className="col-4"></div>
                    </div>
                </div>
            </React.Fragment>
        );
    } else if (!pallet && !palletEditRedux.palletDetailsLoding && !palletEditRedux.palletDetailsError) {
        return (
            <div id="div-error" className={`alert alert-danger`} role="alert">
                Resources.PalletNotFound
            </div>
        );
    } else {
        return (
            <React.Fragment>
                <div className="container mw-100">
                    <div className="row">
                        <div className="col-8"></div>
                        <div className="col-4">
                            <span className="float-right">
                                <div className="input-group mb-3">
                                    <input
                                        type="text"
                                        id="txtSsccSearch"
                                        className="form-control"
                                        placeholder="SSCC"
                                        aria-label="sscc"
                                        aria-describedby="sscc-search"
                                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                            onlyOnEnter(e, () => {
                                                searchSccc(e.currentTarget.value);
                                            });
                                        }}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            dispatch(onSetSsccSearch(e.currentTarget.value));
                                        }}
                                    />
                                    <div className="input-group-append">
                                        <button
                                            id="btnSsccSearch"
                                            className="btn  btn-primary"
                                            type="button"
                                            onClick={e => {
                                                searchSccc(palletCacheRedux.palletSearch.ssccSearch);
                                            }}
                                        >
                                            <span
                                                id="btnRefreshSpinner"
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-hidden="true"
                                                style={{ display: "none" }}
                                            ></span>
                                            {t("main.search")}
                                        </button>
                                    </div>
                                </div>
                            </span>
                        </div>
                    </div>
                </div>
                <div className="container mw-100">
                    <div className="h4">{t("main.palletDetail")}</div>

                    <div className="row" id="infoRow02">
                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtSSCC">
                                {t("main.sscc")}
                            </label>
                            <input
                                type="text"
                                className="form-control font-weight-bold"
                                id="txtSSCC"
                                aria-describedby="SSCC"
                                required
                                readOnly
                                value={pallet.sscc}
                            />
                        </div>

                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtRank">
                                {t("main.rank")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtRank"
                                aria-describedby="Rank"
                                required
                                readOnly
                                value={pallet.sequenceNo}
                            />
                        </div>

                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtBatch">
                                {t("main.batch")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtBatch"
                                aria-describedby="Batch"
                                required
                                readOnly
                                value={pallet.batch ?? ""}
                            />
                        </div>
                    </div>
                    <div className="row" id="infoRow03">
                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtStatus">
                                {t("main.status")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtStatus"
                                aria-describedby="Status"
                                required
                                readOnly
                                value={renderPalletStatus(pallet.statusId, aplSettings.culture)}
                            />
                        </div>

                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtCondition">
                                {t("main.condition")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtCondition"
                                aria-describedby="Condition"
                                required
                                readOnly
                                value={renderPalletCondition(pallet.conditionId, aplSettings.culture)}
                            />
                        </div>

                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtLot">
                                {t("main.lot")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtLot"
                                aria-describedby="Lot"
                                required
                                readOnly
                                value={pallet.lot ?? ""}
                            />
                        </div>
                    </div>
                    <div className="row" id="infoRow04">
                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtPrintDate">
                                {t("main.printDate")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtPrintDate"
                                aria-describedby="PrintDate"
                                required
                                readOnly
                                value={momentDtoToLocalStringByCulture(pallet.printDate, aplSettings.culture)}
                            />
                        </div>

                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtProdDate">
                                {t("main.prodDate")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtProdDate"
                                aria-describedby="ProdDate"
                                required
                                readOnly
                                value={momentDtoToLocalStringByCulture(pallet.dtoPalletProductionDate, aplSettings.culture)}
                            />
                        </div>
                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txt-delivery-note">
                                {translateString("deliveryNote", aplSettings.culture)}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="delivery-note"
                                aria-describedby="delivery-note"
                                required
                                readOnly
                                value={pallet.deliveryNote}
                            />
                        </div>
                    </div>
                    <div className="row" id="infoRow01">
                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtWorkOrder">
                                {t("main.workOrder")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtWorkOrder"
                                aria-describedby="WorkOrder"
                                required
                                readOnly
                                value={pallet.workOrder}
                            />
                        </div>

                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtInitQty">
                                {t("main.initQty")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtInitQty"
                                aria-describedby="txtInitQty"
                                required
                                readOnly
                                value={pallet.initialQty}
                            />
                        </div>

                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtActQty">
                                {t("main.actQty")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtActQty"
                                aria-describedby="ActualQty"
                                required
                                readOnly
                                value={pallet.actualQty}
                            />
                        </div>
                    </div>
                    <div className="row" id="infoRow06">
                        <div className="col-8">
                            <label className="margin-bottom-02em" htmlFor="txtPartDescription">
                                {t("main.partDescription")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtPartDescription"
                                aria-describedby="PartDescription"
                                required
                                readOnly
                                value={pallet.description}
                            />
                        </div>
                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtSpeakingPartNo">
                                {t("main.material12")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtSpeakingPartNo"
                                aria-describedby="SpeakingPartNo"
                                required
                                readOnly
                                value={pallet.speakingPartNo ?? ""}
                            />
                        </div>
                    </div>
                    <div className="row" id="infoRow07">
                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtColor">
                                {t("main.color")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtColor"
                                aria-describedby="Color"
                                required
                                readOnly
                                value={pallet.glassColor ?? ""}
                            />
                        </div>
                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtBaseProduct">
                                {t("main.baseProduct")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtBaseProduct"
                                aria-describedby="BaseProduct"
                                required
                                readOnly
                                value={pallet.glassBaseProduct ?? ""}
                            />
                        </div>
                        <div className="col-4">
                            <label className="margin-bottom-02em" htmlFor="txtExtBarcode">
                                {t("main.extBarcode")}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="txtExtBarcode"
                                aria-describedby="ExtBarcode"
                                required
                                readOnly
                                value={pallet.vendorPalIdent ?? ""}
                            />
                        </div>
                    </div>
                    <br />

                    <span className="float-left">
                        {/* <!-- previous next buttons --> */}
                        <button
                            type="button"
                            className="btn btn-primary "
                            style={{ width: "7.5em" }}
                            onClick={() => {
                                getPalletByOffset(-1);
                            }}
                            id="palstat-edit-btn-previous"
                        >
                            {t("main.previous")}
                        </button>
                        &nbsp;
                        <button
                            type="button"
                            className="btn btn-primary "
                            style={{ width: "7.5em" }}
                            onClick={() => {
                                getPalletByOffset(+1);
                            }}
                            id="palstat-edit-btn-next"
                        >
                            {t("main.next")}
                        </button>
                    </span>

                    <span className="float-right">
                        {buttonsOptions().unblock ? (
                            <React.Fragment>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        buttonsOptions().unblockClick();
                                    }}
                                    id="palstat-edit-btn-unblock"
                                >
                                    {t("main.unblock")}
                                </button>
                                &nbsp;
                            </React.Fragment>
                        ) : (
                            ""
                        )}
                        {buttonsOptions().block ? (
                            <React.Fragment>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        buttonsOptions().blockClick();
                                    }}
                                    id="palstat-edit-btn-block"
                                >
                                    {t("main.block")}
                                </button>
                                &nbsp;
                            </React.Fragment>
                        ) : (
                            ""
                        )}
                        {printButtonShown || buttonsOptions().reprint ? (
                            <React.Fragment>
                                <button
                                    type="button"
                                    className={`btn ${buttonsOptions().reprint ? "btn-primary" : "btn-danger"}`}
                                    onClick={() => {
                                        buttonsOptions().reprintClick();
                                    }}
                                    id="palstat-edit-btn-print"
                                >
                                    {t("main.print")}
                                </button>
                                &nbsp;
                            </React.Fragment>
                        ) : (
                            ""
                        )}
                        {buttonsOptions().scrap && !aplSettings.isUsSite ? (
                            <React.Fragment>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        buttonsOptions().scrapClick();
                                    }}
                                    id="palstat-edit-btn-scrap"
                                >
                                    {t("main.scrap")}
                                </button>
                                &nbsp;
                            </React.Fragment>
                        ) : (
                            ""
                        )}
                        {buttonsOptions().delete ? (
                            <React.Fragment>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        buttonsOptions().deleteClick();
                                    }}
                                    id="palstat-edit-btn-delete"
                                >
                                    {t("main.delete")}
                                </button>
                                &nbsp;
                            </React.Fragment>
                        ) : (
                            ""
                        )}
                        {aplSettings.siteId === 9 && buttonsOptions().delete ? (
                            <button
                                className="btn btn-primary"
                                id="palstat-edit-btn-part-pallet"
                                onClick={buttonsOptions().partPalletClick}
                            >
                                {t("main.partPallet")}
                            </button>
                        ) : (
                            ""
                        )}
                        <span
                            onDoubleClick={() => {
                                if (globalAdminPerm) setPrintButtonShown(true);
                            }}
                        >
                            &nbsp; &nbsp; &nbsp;
                        </span>

                        <button
                            type="button"
                            className="btn btn-primary "
                            onClick={() => {
                                navigate("/palstat");
                            }}
                            id="palstat-edit-btn-back"
                        >
                            {t("main.back")}
                        </button>
                    </span>
                </div>
                <br />
                <br />
                <div className="container mw-100">
                    <div className="h4">{t("main.defects")}</div>
                    <DtGrid2 options={defectGridOptions} />

                    {/* <br />
                    <div className="h4">{t("main.traceabilityInfo")}</div>
                    <DtGrid2 options={traceabilityGridOptions} /> */}

                    <br />
                    <div className="h4">{t("main.statusHistory")}</div>
                    <DtGrid2 options={movementGridOptions} />
                </div>
            </React.Fragment>
        );
    }
}
